import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { getScreenWidth } from '../../utils/LayoutUtility';
import FastImageView from '../FastImageView';
import { getMinifiedImage } from '../../utils/ImageUtility';
import { DOWNLOAD_APP_MODAL_TYPE, SCREEN_CONSTANTS } from '../../config/ScreenConstants';
import Utility from '../../utils/Utility';
import { PERMISSION_STORE, REMOTE_CONFIG_KEYS } from '../../config/Constants';
import { navigationRef } from '../../navigator/NavigationService';
import images from '../../theme/Images';
import { isBlank, isDesktop, isIOS, isPresent, isWeb, isWebAndroid, isWebIOS } from '../../utils/BooleanUtility';
import { fetchHistoryRequest, saveSelfieData } from '../../actions/SkinSelfieAnalysisActions';
import colors from '../../theme/Colors';
import CardSelfie from './CardSelfie';
import AppConfig from '../../config/AppConfig';
import Config from 'react-native-config';
import { getStoreRef } from '../../store/StoreUtility';
import DebouncedTouchableWithoutFeedback from '../shared/DebouncedTouchableWithoutFeedback';
import { jsonParser } from '../../utils/JsonUtility';
import RemoteConfig from '../../utils/RemoteConfig';

const SelfieCardAnalysis = (props) => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const {
    itemData: {
      title = '',
      body = '',
      background_image_url = '',
      arguments: argument = {},
    } = {},
  } = props;
  const { labelColor } = argument;
  const [imgUrl, setImgUrl] = useState(null);
  const [score, setScore] = useState(null);
  const [data, setData] = useState([]);
  const userSelfieData = useSelector(
    (state) => state.UserAccountInfo?.selfieAnalysis,
  );
  const selfie_image = useSelector(
    (state) => state.UserAccountInfo?.selfieAnalysis?.selfie_image,
  );
  const selfieAttributes = userSelfieData?.my_attributes_values;
  const selfieNotUploadModal = jsonParser(RemoteConfig.getValue(REMOTE_CONFIG_KEYS.selfie_not_upload_modal)) || {};
  const [isAppInstallPromptVisibleMobileView, setAppInstallPromptVisibleMobileView] = useState(false);

  const requiredAttributes = [
    'Acne',
    'Smoothness',
    'Uneven Skin',
    'Texture',
    'Redness',
    'Pigmentation',
  ];

  const mapAttributes = (result) => {
    return result.filter((item) =>
      requiredAttributes.includes(item.attributes.analysis_attribute),
    ).map((item) => ({
      attribute: item.attributes.analysis_attribute,
      score: item.attributes.score,
      severity: item.attributes.severity,
    }));
  };

  const navigateToSelfie = () => {
    if (isDesktop()) {
      navigation.navigate(SCREEN_CONSTANTS.APP_INSTALL_PROMPT, {
        from: location.pathname,
      });
      return;
    }
    if (isWebIOS() || isWebAndroid()){
      setAppInstallPromptVisibleMobileView(true);
      return;
    }
    dispatch(saveSelfieData({ uploadedSelfieStatus: '' }));
    Utility.checkPermissionStatic(
      PERMISSION_STORE.camera,
      true,
      (permissionStatus) => {
        if (
          permissionStatus === 'granted' ||
          permissionStatus === 'authorized'
        ) {
          navigationRef.navigate('TakeSkinSelfie', {
            previousScreen: 'ForYou',
          });
        } else if (
          permissionStatus === 'never_ask_again' ||
          permissionStatus === 'denied'
        ) {
          Linking.openSettings();
        }
      },
    );
  };

  const handleFetchResult = () => {
    const params = { startDate: '', endDate: '', has_orbo_content: true };
    dispatch(
      fetchHistoryRequest(params, (success, response) => {
        if (success) {
          const Result = response?.objects[0]?.included;
          const overAllScore =
            response.objects[0]?.data?.attributes?.skin_score;
          setData(mapAttributes(Result));
          setImgUrl(response?.objects[0]?.data?.attributes?.image);
          setScore(overAllScore);
        } else {
          setData('');
          setScore('');
          setImgUrl('');
          console.error('API Error');
        }
      }),
    );
  };

  const onCardPress = () => {
    if (isBlank(data)) {
      navigateToSelfie();
    } else {
      navigation.navigate(SCREEN_CONSTANTS.PAST_SKIN_ANALYSIS, { index: 0 });
    }
  };

  const imageUrl =
    imgUrl ||
    'https://cdn3.foxy.in/media/app_asset/image/9/profile_pic_placeholder.png';
  const headerTitle = isBlank(data) ? 'Understand Your Acne' : title;
  const headerSubTitle = isBlank(data) ? 'Take an AI Skin Analysis' : body;
  const imageArr =
    isPresent(selfie_image) && isPresent(selfieAttributes)
      ? [selfie_image, imageUrl]
      : [imageUrl];
  const updateImageArr = () => {
    if (imageArr.length === 2) {
      imageArr.splice(0, 1);
    }
  };

  useFocusEffect(
    useCallback(() => {
      const uploadedSelfieStatus = userSelfieData?.uploadedSelfieStatus;
      if (uploadedSelfieStatus === 'failed') {
        navigation.navigate('SelfieNotUploadedModal', {
          heading: selfieNotUploadModal.heading,
          subHeading: selfieNotUploadModal.subHeading,
          imageArr,
          updateImageArr,
          selfiePresent: isPresent(imgUrl),
        });
        return;
      }
      handleFetchResult();
    }, [userSelfieData]),
  );

  if (isAppInstallPromptVisibleMobileView) {
    const data =
      jsonParser(
        RemoteConfig.getValue(
          REMOTE_CONFIG_KEYS.text_message_for_download_modal,
        ),
      ) || {};
    const { title = '', subtitle = '' } = data[DOWNLOAD_APP_MODAL_TYPE.selfie] || {};
    navigation.navigate(SCREEN_CONSTANTS.DOWNLOAD_APP_MODAL, {
      title: title,
      subtitle: subtitle,
      setVisibility: setAppInstallPromptVisibleMobileView,
    });
  }
  
  return (
    <>
      <View style={styles.headingContainer}>
        <View>
          <Text style={styles.headingText}>{headerTitle}</Text>
          <Text style={styles.subHeadingText}>{headerSubTitle}</Text>
        </View>
        <TouchableOpacity
          style={styles.buttonStyle}
          onPress={navigateToSelfie}
        >
          <FastImageView
            source={{
              uri: getMinifiedImage(
                images.selfie_camera_icon,
                styles.buttonImage.width,
                styles.buttonImage.height,
              ),
            }}
            style={styles.buttonImage}
          />
          <Text style={styles.buttonTextStyle}>
            {isBlank(data) ? 'Try Now' : 'Retake'}
          </Text>
        </TouchableOpacity>
      </View>
      <DebouncedTouchableWithoutFeedback onPress={onCardPress}>
        <CardSelfie
          data={data}
          score={score}
          imageArr={imageArr}
          background_image_url={background_image_url}
          labelColor={labelColor}
        />
      </DebouncedTouchableWithoutFeedback>
    </>
  );
};

const styles = StyleSheet.create({
  headingContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingTop: 12,
  },
  container: {
    flexDirection: 'row',
    width: getScreenWidth() - 24,
    height: 280,
    marginTop: 50,
    alignContent: 'space-between',
  },
  imageContainer: {
    width: getScreenWidth() - 24,
    height: isIOS() ? 330 : 370,
    alignSelf: 'center',
  },
  profileImage: {
    width: getScreenWidth() * 0.4,
    height: getScreenWidth() * 0.4,
    borderRadius: (getScreenWidth() * 0.4) / 2,
    borderWidth: 5,
    borderColor: 'white',
    marginHorizontal: 20,
    marginTop: 30,
  },
  labelContainer: {
    flexDirection: 'row',
    position: 'absolute',
    alignItems: 'center',
    alignSelf: 'center',
    width: getScreenWidth() * 0.7,
  },
  scoreContainer: {
    width: getScreenWidth() * 0.7,
    height: getScreenWidth() * 0.7,
    alignSelf: 'center',
  },
  percentageCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    // marginBottom: 5,
    backgroundColor: 'white',
    position: 'absolute',
    left: -24,
    zIndex: 1,
  },
  percentageText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: 'black',
  },
  labelText: {
    fontSize: 12,
    color: '#000000',
    textAlign: 'left',
    fontWeight: '600',
    marginLeft: 12,
  },
  labelTextContainer: {
    backgroundColor: '#FFFFFF99',
    borderRadius: 24,
    width: '40%',
    padding: 2,
  },
  headingText: {
    fontFamily: 'Roboto',
    fontSize: 18,
    fontWeight: '700',
    color: '#000000',
  },
  subHeadingText: {
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: '400',
    color: '#979BAA',
    paddingBottom: 12,
    lineHeight: 16,
    marginTop: 4,
  },
  buttonImage: {
    width: 20,
    height: 20,
    marginRight: 2,
  },
  buttonStyle: {
    marginTop: 4,
    width: 95,
    backgroundColor: Config.SELFIE_BUTTON_COLOR || colors.black,
    borderRadius: 30,
    height: 35,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  buttonTextStyle: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'Roboto',
  },
  overAllSkinScore: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'white',
    position: 'absolute',
    bottom: isIOS() ? 35 : 55,
    right: 20,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default SelfieCardAnalysis;
